import React, { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { faTwitter } from '@fortawesome/free-brands-svg-icons/faTwitter';
import twttr from 'twitter-text';

import WordCounter from '@buyside/components/presentation/Twitter/PostTweet/wordCounter';
import Button, { BUTTON_TYPES } from '@buyside/components/presentation/Button/button';
import ProfileImage, {
  IMAGE_SIZES
} from '@buyside/components/presentation/ProfileImage/profileImage';
import { oauth2Redirect } from '@buyside/util/urlHelper';

import {
  CreateTweetMutationVariables,
  useCreateTweetMutation,
  useTwitterCurrentUserQuery
} from '@interfaces/graphql-typings';

import styles from '@buyside/components/presentation/Twitter/twitterStyles.scss';

const PostTweetForm: React.FC<{
  initialTweetMsg?: string;
}> = ({ initialTweetMsg = '' }) => {
  const [tweetBody, setTweetBody] = useState<string>('');

  const { data: queryData } = useTwitterCurrentUserQuery();

  const [createTweet, { data, error }] = useCreateTweetMutation();

  const {
    formState: { isSubmitting },
    handleSubmit,
    register,
    setValue
  } = useForm<CreateTweetMutationVariables>();

  const onSubmit: SubmitHandler<CreateTweetMutationVariables> = () =>
    createTweet({ variables: { tweetBody } });

  if (!queryData || !queryData.currentUser) return null;

  const { hasTwitterToken, profileImageUrl } = queryData.currentUser;

  const showAuthLink =
    !hasTwitterToken ||
    Boolean(error?.graphQLErrors && error.graphQLErrors[0].message.includes('Please authorize'));

  const tweetText = tweetBody || initialTweetMsg;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={styles.postTweetForm}>
      <div>
        <ProfileImage source={profileImageUrl} size={IMAGE_SIZES.SMALLER} />
        <textarea
          id="body"
          disabled={isSubmitting}
          {...register('tweetBody')}
          name="body"
          onChange={(e): void => {
            setTweetBody(e.target.value);
            setValue('tweetBody', e.target.value, { shouldDirty: false });
          }}
          placeholder="What would you like to share?"
          value={tweetText}
        />
      </div>
      <footer className={styles.tweetFormFooter}>
        <div className={styles.errorContainer}>
          {error && <>{error.graphQLErrors[0].message}</>}
        </div>
        {twttr.parseTweet(tweetText).weightedLength > 0 && (
          <WordCounter weightedLength={twttr.parseTweet(tweetText).weightedLength} />
        )}
        <div className={styles.buttonContainer}>
          {showAuthLink && (
            <Button text="Authorize on Twitter" url={oauth2Redirect()} faIcon={faTwitter} />
          )}
          {data?.createTweet?.tweetUrl && (
            <Button
              text="See your new tweet"
              url={data.createTweet.tweetUrl}
              faIcon={faTwitter}
              target="_blank"
            />
          )}
          <Button
            isLoading={isSubmitting}
            text="Post"
            type={BUTTON_TYPES.PRIMARY}
            disabled={!twttr.parseTweet(tweetText).valid || isSubmitting || showAuthLink}
          />
        </div>
      </footer>
    </form>
  );
};

export default PostTweetForm;
